import type {
  MotionTokens,
  RadiusTokens,
  SpacingTokens,
  Theme,
  TypographyTokens,
} from '../../theme/contract';

/**
 * Command Center — the parent/admin theme. Token values are extracted from
 * `docs/mockups/10-command-center.html`: indigo brand, sky/green/amber/rose status colors, cool
 * neutral canvas, tight radii, Inter font, crisp data-dense shadows.
 *
 * Non-color tokens are shared between light and dark; only the color set differs.
 */

const radius: RadiusTokens = {
  sm: '8px',
  md: '10px',
  lg: '14px',
  card: '14px',
  pill: '999px',
};

const spacing: SpacingTokens = {
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '32px',
};

const typography: TypographyTokens = {
  fontSans: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontDisplay: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontMono: "ui-monospace, 'SFMono-Regular', 'Menlo', monospace",
  sizeXs: '11px',
  sizeSm: '12px',
  sizeBase: '13px',
  sizeMd: '14px',
  sizeLg: '17px',
  sizeXl: '22px',
  sizeXxl: '31px',
  weightNormal: '400',
  weightMedium: '500',
  weightSemibold: '600',
  weightBold: '700',
  trackingTight: '-0.028em',
  leadingTight: '1.08',
  leadingNormal: '1.45',
};

const motion: MotionTokens = {
  durationFast: '100ms',
  durationNormal: '160ms',
  durationSlow: '260ms',
  easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeBounce: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

export const commandCenter: Theme = {
  id: 'command-center',
  light: {
    color: {
      bg: '#F6F8FB',
      surface: '#FFFFFF',
      surfaceMuted: '#EEF1F7',
      primary: '#4F46E5',
      primaryFg: '#FFFFFF',
      secondary: '#0EA5E9',
      secondaryFg: '#FFFFFF',
      accent: '#EEF0FE',
      accentFg: '#4F46E5',
      text: '#0F172A',
      textMuted: '#64748B',
      border: '#E6EAF0',
      input: '#D8DEE8',
      // Darkened ink so the status pills clear WCAG AA (≥4.5:1) on their soft tint backgrounds:
      // success 4.56:1 on `success-fg`, warning 4.60:1 on `warning-fg` (and ≥5:1 on white surface).
      success: '#15803D',
      successFg: '#E8F8EE',
      warning: '#B45309',
      warningFg: '#FEF4E2',
      danger: '#F43F5E',
      dangerFg: '#FFFFFF',
      // Effort-stars (✨) = violet — the third currency, distinct from coins (gold) / cash (green).
      star: '#6D28D9',
      starFg: '#EDE9FE',
      ring: '#4F46E5',
    },
    // Crisp, data-dense status palette: indigo → sky → green → amber → rose → violet. Built for the
    // command center's economy charts (Step 2) where earn-vs-spent legibility matters.
    chart: {
      '1': '#4F46E5',
      '2': '#0EA5E9',
      '3': '#15803D',
      '4': '#B45309',
      '5': '#F43F5E',
      '6': '#6D28D9',
      grid: '#E6EAF0',
      axis: '#64748B',
      cursor: '#EEF1F7',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 1px 3px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04)',
      md: '0 4px 10px rgba(15,23,42,.08), 0 1px 3px rgba(15,23,42,.05)',
      lg: '0 40px 80px -32px rgba(15,23,42,.34), 0 16px 36px -22px rgba(15,23,42,.22)',
      card: '0 1px 2px rgba(15,23,42,.04), 0 0 0 1px rgba(15,23,42,.03)',
      nav: '0 -4px 16px rgba(15,23,42,.06)',
    },
    motion,
  },
  dark: {
    color: {
      bg: '#0B1120',
      surface: '#111A2E',
      surfaceMuted: '#19233B',
      primary: '#818CF8',
      primaryFg: '#0B1120',
      secondary: '#38BDF8',
      secondaryFg: '#04141F',
      accent: '#1E2747',
      accentFg: '#A5B4FC',
      text: '#E6EAF2',
      textMuted: '#94A3B8',
      border: '#243049',
      input: '#2C3954',
      success: '#34D399',
      successFg: '#0C2A1E',
      warning: '#FBBF24',
      warningFg: '#332507',
      danger: '#FB7185',
      dangerFg: '#2A0B12',
      star: '#A78BFA',
      starFg: '#241F33',
      ring: '#818CF8',
    },
    chart: {
      '1': '#818CF8',
      '2': '#38BDF8',
      '3': '#34D399',
      '4': '#FBBF24',
      '5': '#FB7185',
      '6': '#A78BFA',
      grid: '#243049',
      axis: '#94A3B8',
      cursor: '#19233B',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 1px 3px rgba(0,0,0,.50), 0 1px 2px rgba(0,0,0,.40)',
      md: '0 4px 10px rgba(0,0,0,.55), 0 1px 3px rgba(0,0,0,.45)',
      lg: '0 40px 80px -32px rgba(0,0,0,.70), 0 16px 36px -22px rgba(0,0,0,.55)',
      card: '0 1px 2px rgba(0,0,0,.40), 0 0 0 1px rgba(255,255,255,.04)',
      nav: '0 -4px 16px rgba(0,0,0,.45)',
    },
    motion,
  },
};
